// ============================================================
// INPUT HANDLING
// ============================================================

import { BATTLE_COLS, BATTLE_ROWS } from './constants.ts';
import { canvas, W, H, ZHY, FIELD_Y, DEPLOY_PANEL_TOP, eventToLogical, screenToCell } from './canvas.ts';
import { Game, GS } from './Game.ts';
import { Deployment } from './Deployment.ts';
import { Renderer } from './Renderer.ts';
import { showPrompt } from './prompt.ts';
import { DECK_TEMPLATES } from './cards/DeckTemplates.ts';
import { LEVELS } from './LevelData.ts';

// Phase 3d Step 3: hit-test against the menu buttons drawn by Renderer.
function hitMenuButton(x: number, y: number, idx: number): boolean {
  const r = Renderer.menuButtonRect(idx);
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

/** Generic rect hit-test. */
function hitRect(x: number, y: number, r: { x: number; y: number; w: number; h: number }): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

export class Input {
  private g: Game;
  private sx = 0;
  private sy = 0;
  private dragging = false;
  /** Whether the swipe already triggered a swap during this touch */
  private swipeFired = false;
  /** Whether we're currently drag-drawing a path in deployment */
  private drawingPath = false;

  constructor(game: Game) {
    this.g = game;
    // M4 — events come in client px; eventToLogical maps through the
    // letterbox (CSS scale + centering) into the fixed 1280×720 logical
    // coordinate space that all drawing / hit-testing uses.
    canvas.addEventListener('mousedown', (e) => { const p = eventToLogical(e.clientX, e.clientY); this._down(p.x, p.y); });
    canvas.addEventListener('mouseup', (e) => { const p = eventToLogical(e.clientX, e.clientY); this._up(p.x, p.y); });
    canvas.addEventListener('mousemove', (e) => {
      // 拖曳時、或戰鬥選位時（讓準星/範圍跟著滑鼠 hover）都更新。
      if (this.dragging || (this.g.state === GS.SCHED && this.g.targeting)) {
        const p = eventToLogical(e.clientX, e.clientY); this._move(p.x, p.y);
      }
    });
    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const t = e.touches[0];
      const p = eventToLogical(t.clientX, t.clientY);
      this._down(p.x, p.y);
    }, { passive: false });
    canvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      const t = e.changedTouches[0];
      const p = eventToLogical(t.clientX, t.clientY);
      this._up(p.x, p.y);
    }, { passive: false });
    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (this.dragging) {
        const t = e.touches[0];
        const p = eventToLogical(t.clientX, t.clientY);
        this._move(p.x, p.y);
      }
    }, { passive: false });
  }

  private _down(x: number, y: number): void {
    this.sx = x; this.sy = y;
    this.dragging = true;
    this.swipeFired = false;
    const g = this.g;
    // Surrender confirm modal — absorbs all _down (taps land on the
    // modal buttons in _up); also prevent any other handling.
    if (g.surrenderConfirmActive) {
      this.dragging = false;
      return;
    }
    // --- Deployment: start drag from palette, select unit, or start path draw ---
    if (g.state === GS.DEPLOY) {
      const dep = g.deployment;
      this.drawingPath = false;
      // Check palette area (bottom zone)
      if (y >= DEPLOY_PANEL_TOP) {
        this._deployPaletteDown(x, y, dep);
        return;
      }
      // Check battlefield grid (iso) — behavior depends on dep.pathMode
      if (y < DEPLOY_PANEL_TOP) {
        const { cx: col, cy: row } = screenToCell(x, y);
        if (col >= 0 && col < BATTLE_COLS && row >= 0 && row < BATTLE_ROWS) {
          const idx = dep.unitAt(col, row);
          if (dep.pathMode) {
            // PATH MODE: tap unit → select for path; tap-again on same → clear path.
            if (idx >= 0) {
              if (dep.selectedUnit === idx) dep.clearPath();
              dep.selectedUnit = idx;
              dep.dragPos = { x, y };
              this.drawingPath = true;
            } else if (dep.selectedUnit >= 0) {
              // Tap adjacent empty cell to extend path one tile
              if (dep.addPathPoint(col, row)) this.drawingPath = true;
            }
          } else {
            // PLACE MODE: tap unit arms a grid drag (reposition or remove).
            if (idx >= 0) {
              dep.selectedUnit = idx;
              dep.dragRemoveIdx = idx;
              dep.dragPos = { x, y };
            }
            // Empty cell taps: leave selection alone
          }
        }
      }
      return;
    }

    // Targeting (iso) — battle target picking uses the 45° inverse projection.
    if (g.targeting && y < ZHY) {
      const { cx, cy } = screenToCell(x, y);
      if (cx >= 0 && cx < BATTLE_COLS && cy >= 0 && cy < BATTLE_ROWS) {
        g.targetPos = { x: cx, y: cy };
      }
    }
  }

  /** Handle palette area press during deployment */
  private _deployPaletteDown(x: number, y: number, dep: Deployment): void {
    const palY = DEPLOY_PANEL_TOP + 6;
    const slotH = 42;
    const palX = 10;
    const slotW = (W - 20) / dep.palette.length;

    for (let i = 0; i < dep.palette.length; i++) {
      const sx = palX + i * slotW;
      if (x >= sx && x <= sx + slotW && y >= palY && y <= palY + slotH) {
        const slot = dep.palette[i];
        if (slot.placed < slot.max) {
          dep.selectedPalette = i;
          dep.dragging = true;
          dep.dragType = slot.type;
          dep.dragPos = { x, y };
        }
        return;
      }
    }
  }

  private _move(x: number, y: number): void {
    const g = this.g;

    // Deployment movement handler. Modes:
    //   pathMode ON  → drag from selected unit extends its path
    //   pathMode OFF → drag a placed unit; ghost follows cursor (drop in _up)
    if (g.state === GS.DEPLOY) {
      const dep = g.deployment;
      // Palette unit drag (palette → grid)
      if (dep.dragging) {
        dep.dragPos = { x, y };
        return;
      }
      // Path drawing only in path mode
      if (dep.pathMode && this.drawingPath && dep.selectedUnit >= 0 && y < DEPLOY_PANEL_TOP) {
        const { cx: col, cy: row } = screenToCell(x, y);
        if (col >= 0 && col < BATTLE_COLS && row >= 0 && row < BATTLE_ROWS) {
          dep.addPathPoint(col, row);
        }
      }
      // Place-mode grid drag: keep tracking ghost position
      if (dep.dragRemoveIdx >= 0) {
        dep.dragPos = { x, y };
      }
      return;
    }

    // Targeting drag (iso)
    if (g.targeting && y < ZHY) {
      const { cx, cy } = screenToCell(x, y);
      if (cx >= 0 && cx < BATTLE_COLS && cy >= 0 && cy < BATTLE_ROWS) {
        g.targetPos = { x: cx, y: cy };
      }
    }
  }

  private _up(x: number, y: number): void {
    const g = this.g;
    const dx = x - this.sx, dy = y - this.sy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    this.dragging = false;
    g.pressedCell = null;

    // Surrender confirm modal — Confirm / Cancel buttons. Absorb all
    // other input until the modal is dismissed.
    if (g.surrenderConfirmActive) {
      if (hitRect(x, y, Renderer.surrenderConfirmRect())) {
        g.confirmSurrender();
      } else if (hitRect(x, y, Renderer.surrenderCancelRect())) {
        g.dismissSurrenderConfirm();
      }
      return;
    }

    // Card Tactician (M4c) — 投降按鈕（左欄）：直接開確認 modal（不長按）。
    if (g.cardMode && (g.state === GS.SCHED || g.state === GS.EXEC)
        && hitRect(x, y, Renderer.cardSurrenderRect())) {
      g.openSurrenderConfirm();
      return;
    }

    // --- Menu tap ---
    if (g.state === GS.MENU) {
      //   Non-net layout:  Test idx=0
      //   Net layout:      Test idx=0, Create idx=1, Join idx=2, Server idx=3
      if (g.netMode) {
        if (hitMenuButton(x, y, 0)) { g.startSinglePlayer(); return; }
        if (hitMenuButton(x, y, 1)) { void g.startCreateRoom(); return; }
        if (hitMenuButton(x, y, 2)) {
          // In-app prompt — works in Electron sandbox + Capacitor WebView
          // where window.prompt() silently returns null.
          void showPrompt('Enter room code:').then((code) => {
            if (code) void g.startJoinRoom(code);
          });
          return;
        }
        // Phase 5a Step 1: idx=3 = Server URL editor.
        if (hitMenuButton(x, y, 3)) { g.promptServerUrl(); return; }
        return;
      }
      // Non-net mode — single-player only.
      if (hitMenuButton(x, y, 0)) { g.startSinglePlayer(); return; }
      return;
    }

    // --- Lobby tap (Cancel button) ---
    if (g.state === GS.LOBBY) {
      // Phase 3d Step 5 fix: Cancel moved to button idx 3 to avoid
      // overlap with the status message. Stay in sync with Renderer._lobby.
      if (hitMenuButton(x, y, 3)) {
        // Reset back to MENU and tear down the half-open adapter.
        try { (g.adapter as any)?.disconnect?.(); } catch { /* ignore */ }
        g.adapter = undefined as any;
        g.gameMode = 'single';
        g.myTeam = 0;
        g.roomCode = null;
        g.lobbyMessage = '';
        g.opponentSessionId = null;
        g.state = GS.MENU;
      }
      return;
    }

    // --- Level select (single) ---
    if (g.state === GS.LEVELS) {
      for (let i = 0; i < LEVELS.length; i++) {
        if (hitRect(x, y, Renderer.levelButtonRect(i))) { g.selectLevel(LEVELS[i].id); return; }
      }
      if (hitRect(x, y, Renderer.levelBackRect())) { g.restart(); return; }
      return;
    }

    // --- Deployment interactions ---
    if (g.state === GS.DEPLOY) {
      this._deployUp(x, y);
      return;
    }

    // Targeting confirm — Session 8 fix: only commit if the lift
    // actually happened on the battlefield. Earlier this fired any
    // time `g.targetPos` existed, which meant tapping the Back
    // button (in the SCHED panel below ZHY) after a stale targetPos
    // would silently confirm the spell and rob the user of their
    // back-out. The `_down` / `_move` handlers above already gate
    // targetPos updates on `y < ZHY`, so this matching gate keeps
    // confirm consistent with where the user actually clicked.
    if (g.targeting && g.targetPos && y >= FIELD_Y && y < ZHY) {
      g.confirmTarget(g.targetPos.x, g.targetPos.y);
      return;
    }

    // Card Tactician (M4b) — 卡牌 UI 命中（取代三消 tab / 排程面板）。
    // 確認鈕在頂列（y < FIELD_Y，已避開上面的 targeting-confirm）；
    // tick 槽 / 手牌在底部（y > ZHY）。
    if (g.cardMode && g.state === GS.SCHED) {
      // 返回最優先（任何模式都能取消）。
      if (hitRect(x, y, Renderer.cardBackRect())) { g.cardBack(); return; }
      // 搜牌選單：點牌庫選項上手；其餘點擊吞掉（用返回取消）。
      if (g.tutorMode) {
        const opts = g.tutorOptions();
        for (let i = 0; i < opts.length; i++) {
          if (hitRect(x, y, Renderer.tutorOptionRect(i, opts.length))) { g.tutorPick(opts[i].cardId); return; }
        }
        return;
      }
      if (hitRect(x, y, Renderer.cardConfirmRect())) { g.confirmCardSchedule(); return; }
      if (hitRect(x, y, Renderer.cardPassRect())) { g.cardPassTick(); return; }
      // v2 跳 tick 抽牌（單機；skipTickDraw 自帶 guard）。
      if (g.gameMode === 'single' && hitRect(x, y, Renderer.cardDrawRect())) { g.skipTickDraw(); return; }
      const deck = g.playerDeck;
      if (deck) {
        const n = deck.hand.length;
        for (let i = 0; i < n; i++) {
          if (hitRect(x, y, Renderer.cardHandRect(i, n))) { g.cardPickCard(i); return; }
        }
      }
      return; // 卡牌模式吞掉其餘底部點擊（不落到三消面板）
    }

    // Game over restart（cy 須與 Renderer._gameOver 一致）
    if (g.state === GS.OVER) {
      const cy = Math.round(H * 0.56);
      if (y >= cy + 30 && y <= cy + 74 && x >= W * 0.25 && x <= W * 0.75) {
        g.restart();
      }
    }
  }

  /** Handle touch/click release during deployment */
  private _deployUp(x: number, y: number): void {
    const g = this.g;
    const dep = g.deployment;

    // Stop path drag-drawing
    this.drawingPath = false;

    // --- Place-mode grid drag: drop in bottom = remove, on cell = reposition ---
    if (dep.dragRemoveIdx >= 0) {
      const idx = dep.dragRemoveIdx;
      dep.dragRemoveIdx = -1;
      dep.dragPos = null;
      if (y >= DEPLOY_PANEL_TOP) {
        dep.removeUnit(idx);
        dep.selectedUnit = -1;
      } else if (y < DEPLOY_PANEL_TOP) {
        const { cx: col, cy: row } = screenToCell(x, y);
        if (col >= 0 && col < BATTLE_COLS && row >= 0 && row < BATTLE_ROWS) {
          // moveUnit() validates origin / occupancy / player zone internally
          dep.moveUnit(idx, col, row);
        }
      }
      // Otherwise: released outside both zones → cancel (unit stays put)
      return;
    }

    // --- Drop dragged unit onto grid ---
    if (dep.dragging && dep.dragType) {
      dep.dragging = false;
      const { cx: col, cy: row } = screenToCell(x, y);
      if (col >= 0 && col < BATTLE_COLS && row >= 0 && row < BATTLE_ROWS) {
        dep.placeUnit(dep.dragType, col, row);
      }
      dep.dragType = '';
      dep.dragPos = null;
      dep.selectedPalette = -1;
      return;
    }

    // --- Bottom zone button checks ---
    if (y >= DEPLOY_PANEL_TOP) {
      const btnZone = this._getDeployButtons(dep);
      for (const btn of btnZone) {
        if (x >= btn.x && x <= btn.x + btn.w && y >= btn.y && y <= btn.y + btn.h) {
          btn.action();
          return;
        }
      }
      return;
    }

    // Grid tap (unified): selection + path edits are handled entirely
    // via _down + _move. _up has nothing left to do for the grid itself.
  }

  /**
   * Get deployment button layout for hit testing.
   * Row A:  [Draw Paths toggle (40%)] [Confirm / placeholder (60%)]
   * Row B:  [Slot 1] [Slot 2] [Slot 3]
   * Row C:  [↓ Save] [🗑 Clear]
   * Row D:  [Remove selected unit]  (only when a unit is selected)
   */
  _getDeployButtons(dep: Deployment): { x: number; y: number; w: number; h: number; action: () => void }[] {
    const g = this.g;
    // 關卡制（preset）：極簡按鈕組（我方牌組 + Start + 返回關卡）。座標須與
    // Renderer._deployPanelPreset 一致。
    if (dep.rules.kind === 'preset') return this._getDeployButtonsPreset(dep);
    const buttons: { x: number; y: number; w: number; h: number; action: () => void }[] = [];
    const T = DEPLOY_PANEL_TOP;
    const btnH = 30;
    // 所有 Y 座標須與 Renderer._deployPanel 一致（命中矩形同源）。
    const showAiPicker = g.gameMode === 'single';
    const baseY = showAiPicker ? T + 98 : T + 74;

    // --- Row L: 牌組選擇器命中（單機多一行敵方牌組）。 ---
    {
      const leaderY = T + 52;
      const leaderH = 19;
      const aiLeaderY = leaderY + 22;
      const ids = DECK_TEMPLATES.map((d) => d.id);
      const ldrGap = 4;
      const ldrW = (W - W * 0.08 - ldrGap * (ids.length - 1)) / ids.length;
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        const lx = W * 0.04 + i * (ldrW + ldrGap);
        buttons.push({
          x: lx, y: leaderY, w: ldrW, h: leaderH,
          action: () => { g.setPlayerDeck(id); g.slotAction = 'none'; },
        });
        if (showAiPicker) {
          buttons.push({
            x: lx, y: aiLeaderY, w: ldrW, h: leaderH,
            action: () => { g.setAiDeck(id); g.slotAction = 'none'; },
          });
        }
      }
    }

    // --- Row A left: Draw Paths toggle (always present) ---
    const dpW = W * 0.36;
    const cfX = W * 0.04 + dpW + 8;
    const cfW = W - cfX - W * 0.04;
    buttons.push({
      x: W * 0.04, y: baseY, w: dpW, h: btnH,
      action: () => { dep.togglePathMode(); g.slotAction = 'none'; },
    });

    // --- Row A right: Confirm / Start Battle (only when canConfirm) ---
    if (dep.canConfirm()) {
      buttons.push({
        x: cfX, y: baseY, w: cfW, h: btnH,
        action: () => { g.slotAction = 'none'; g.startBattle(); },
      });
    }

    // ----- Manual mode (single / PvP) — full panel -----

    // --- Row B: Save/Load slot row (3 tiles) ---
    const slotY = baseY + btnH + 5;
    const slotH = 22;
    const slotGap = 4;
    const slotW = (W * 0.92 - slotGap * 2) / 3;
    for (let i = 0; i < 3; i++) {
      const sx = W * 0.04 + i * (slotW + slotGap);
      buttons.push({
        x: sx, y: slotY, w: slotW, h: slotH,
        action: () => { g.triggerSlot(i); },
      });
    }

    // --- Row C: Save / Clear toggle ---
    const actY = slotY + slotH + 5;
    const actH = 22;
    const actW = W * 0.44;
    buttons.push({
      x: W * 0.04, y: actY, w: actW, h: actH,
      action: () => { g.toggleSlotAction('save'); },
    });
    buttons.push({
      x: W * 0.52, y: actY, w: actW, h: actH,
      action: () => { g.toggleSlotAction('clear'); },
    });

    // --- Bottom row: Remove(選中單位) 取代 Main Menu（互斥，與 Renderer 一致）---
    const bottomY = actY + actH + 5;
    const bottomH = 26;
    if (dep.selectedUnit >= 0) {
      buttons.push({
        x: W * 0.04, y: bottomY, w: W * 0.92, h: bottomH,
        action: () => {
          dep.removeUnit(dep.selectedUnit);
          dep.selectedUnit = -1;
        },
      });
    } else if (g.gameMode === 'single') {
      buttons.push({
        x: W * 0.25, y: bottomY, w: W * 0.50, h: bottomH,
        action: () => { g.restart(); },
      });
    }

    return buttons;
  }

  /** 關卡制（preset）佈陣按鈕：我方牌組 + Start + 返回關卡。座標鏡像
   *  Renderer._deployPanelPreset。 */
  private _getDeployButtonsPreset(dep: Deployment): { x: number; y: number; w: number; h: number; action: () => void }[] {
    const g = this.g;
    const T = DEPLOY_PANEL_TOP;
    const buttons: { x: number; y: number; w: number; h: number; action: () => void }[] = [];
    const ids = DECK_TEMPLATES.map((d) => d.id);
    const ldrGap = 6;
    const ldrW = (W * 0.92 - ldrGap * (ids.length - 1)) / ids.length;
    const leaderY = T + 34, leaderH = 30;
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      const lx = W * 0.04 + i * (ldrW + ldrGap);
      buttons.push({ x: lx, y: leaderY, w: ldrW, h: leaderH, action: () => { g.setPlayerDeck(id); } });
    }
    const stY = leaderY + leaderH + 14, stH = 44;
    if (dep.canConfirm()) {
      buttons.push({ x: W * 0.04, y: stY, w: W * 0.92, h: stH, action: () => { g.startBattle(); } });
    }
    const bkY = stY + stH + 12, bkH = 30;
    buttons.push({ x: W * 0.30, y: bkY, w: W * 0.40, h: bkH, action: () => { g.backToLevels(); } });
    return buttons;
  }
}
